import { createPortal } from 'react-dom';
import { X, ShoppingCart, Plus, Minus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useCart, getCartShippingProfile } from '../context/CartContext';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CartDrawer() {
  const { items, removeItem, updateQuantity, subtotal, notecardSinglePrice, calendarUnitPrice, isOpen, closeCart } = useCart();
  const shippingProfile = getCartShippingProfile(items);
  const shippingCost = shippingProfile.calc(subtotal);
  const freeShippingThreshold = shippingProfile.threshold;

  const getUnitPrice = (item) => {
    if (item.shop === 'notecards' && item.variant === 'single') return notecardSinglePrice;
    if (item.shop === 'calendar') return calendarUnitPrice;
    return item.price;
  };
  const [checkingOut, setCheckingOut] = useState(false);

  const handleCheckout = async () => {
    if (items.length === 0) return;
    setCheckingOut(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/cart/checkout`, {
        items: items.map(({ cartItemId, ...rest }) => rest),
        origin_url: window.location.origin
      });
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Checkout failed. Please try again.');
      setCheckingOut(false);
    }
  };

  if (!isOpen) return null;

  const totalItems = items.reduce((s, i) => s + i.quantity, 0);

  return createPortal(
    <>
      <div
        onClick={closeCart}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 99998 }}
      />
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: '420px', maxWidth: '100vw',
        background: '#fff', zIndex: 99999,
        display: 'flex', flexDirection: 'column',
        boxShadow: '-4px 0 20px rgba(0,0,0,0.15)'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid #eee',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <ShoppingCart size={22} />
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
              Your Cart
              {totalItems > 0 && (
                <span style={{ fontSize: '14px', color: '#888', fontWeight: '400', marginLeft: '8px' }}>
                  ({totalItems} {totalItems === 1 ? 'item' : 'items'})
                </span>
              )}
            </h2>
          </div>
          <button
            onClick={closeCart}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex' }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Items */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px' }}>
          {items.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#888' }}>
              <ShoppingCart size={48} style={{ marginBottom: '16px', opacity: 0.3 }} />
              <p style={{ margin: 0, fontSize: '16px' }}>Your cart is empty</p>
              <p style={{ margin: '8px 0 0', fontSize: '14px' }}>Add items from any shop to get started.</p>
            </div>
          ) : (
            items.map(item => (
              <div key={item.cartItemId} style={{
                display: 'flex', gap: '14px',
                padding: '16px 0', borderBottom: '1px solid #f0f0f0'
              }}>
                {item.image_url && (
                  <img
                    src={item.image_url}
                    alt={item.product_title}
                    style={{
                      width: '72px', height: '72px', objectFit: 'cover',
                      borderRadius: '6px', flexShrink: 0
                    }}
                  />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: '0 0 2px', fontWeight: '500', fontSize: '14px', lineHeight: '1.4' }}>
                    {item.product_title}
                  </p>
                  {item.variant_label && (
                    <p style={{ margin: '0 0 4px', fontSize: '12px', color: '#888' }}>
                      {item.variant_label}
                    </p>
                  )}
                  {item.special_instructions && (
                    <p style={{ margin: '0 0 6px', fontSize: '11px', color: '#666', fontStyle: 'italic' }}>
                      Note: {item.special_instructions}
                    </p>
                  )}
                  {item.max_quantity != null && item.quantity >= item.max_quantity && (
                    <p style={{ margin: '0 0 6px', fontSize: '11px', color: '#b45309' }}>
                      Only {item.max_quantity} left in stock
                    </p>
                  )}
                  <div style={{
                    display: 'flex', alignItems: 'center',
                    justifyContent: 'space-between', marginTop: '8px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <button
                        onClick={() => updateQuantity(item.cartItemId, item.quantity - 1)}
                        style={{
                          width: '28px', height: '28px', border: '1px solid #ddd',
                          borderRadius: '4px', background: '#fff', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}
                      >
                        <Minus size={12} />
                      </button>
                      <span style={{ fontSize: '14px', fontWeight: '500', minWidth: '20px', textAlign: 'center' }}>
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => updateQuantity(item.cartItemId, item.quantity + 1)}
                        disabled={item.max_quantity != null && item.quantity >= item.max_quantity}
                        style={{
                          width: '28px', height: '28px', border: '1px solid #ddd',
                          borderRadius: '4px', background: '#fff',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          opacity: (item.max_quantity != null && item.quantity >= item.max_quantity) ? 0.4 : 1,
                          cursor: (item.max_quantity != null && item.quantity >= item.max_quantity) ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <Plus size={12} />
                      </button>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ fontWeight: '600', fontSize: '15px' }}>
                        ${(getUnitPrice(item) * item.quantity).toFixed(2)}
                        {item.shop === 'notecards' && item.variant === 'single' && notecardSinglePrice < item.price && (
                          <span style={{ fontSize: '11px', color: '#16a34a', marginLeft: '5px' }}>
                            (${notecardSinglePrice}/ea)
                          </span>
                        )}
                      </span>
                      <button
                        onClick={() => removeItem(item.cartItemId)}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: '#ccc', padding: '4px', display: 'flex'
                        }}
                        aria-label="Remove item"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div style={{ padding: '20px 24px', borderTop: '1px solid #eee', background: '#fafafa' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              <span style={{ fontSize: '16px', color: '#666' }}>Subtotal</span>
              <span style={{ fontSize: '18px', fontWeight: '600' }}>${subtotal.toFixed(2)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              <span style={{ fontSize: '14px', color: '#666' }}>Shipping</span>
              <span style={{ fontSize: '14px', fontWeight: '500', color: shippingCost === 0 ? '#16a34a' : '#333' }}>
                {shippingCost === 0 ? 'Free' : `$${shippingCost.toFixed(2)}`}
              </span>
            </div>
            <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#888' }}>
              {shippingCost === 0
                ? "You've got free shipping!"
                : `Free shipping on orders over $${Math.floor(freeShippingThreshold)} (add $${(freeShippingThreshold - subtotal).toFixed(2)} more)`}
            </p>
            <Button
              onClick={handleCheckout}
              disabled={checkingOut}
              style={{ width: '100%', padding: '14px', fontSize: '16px', fontWeight: '600' }}
            >
              <ShoppingCart size={18} style={{ marginRight: '8px' }} />
              {checkingOut ? 'Processing...' : 'Checkout'}
            </Button>
          </div>
        )}
      </div>
    </>,
    document.body
  );
}
